import { Breakpoint, Typography } from '@altibox/lte-salgskanaler-design-system-component-lib';
import React, { FunctionComponent } from 'react';
import { Button } from '@lyse-as/formds-react';
import {
  MailIcon,
  FavoriteIcon,
  LocalShippingIcon,
  ElectricalServicesIcon,
} from '@lyse-as/formds-react/lib/icon/common';

import { InformationStep } from 'src/components/information-step';
import { ProductInformationStep } from 'src/components/information-step/product-information-step';
import { Link } from 'src/components/link';
import { config } from 'src/config/config';
import { useSelectSubmitShoppingCartState } from 'src/hooks/use-submit-shopping-cart-mutation';
import { navigationService } from 'src/services/navigation-service';
import { selectDeliveryType } from 'src/state/order/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { I18nUtils } from 'src/utils/i18n-utils';
import { i18n } from 'src/i18n';
import { withOrderStep } from 'src/hocs/with-order-step';
import { OrderStep } from 'src/state/order/types';

import { OrderDetails } from './order-details';
import {
  StyledButtonContainer,
  StyledContainer,
  StyledHeading,
  StyledHeadingPlaceholder,
  StyledSuccessItems,
  StyledTitle,
} from './success-page.styled';

export const SuccessPageComponent: FunctionComponent = () => {
  const deliveryType = useAppSelector(selectDeliveryType);
  const { isLoading } = useSelectSubmitShoppingCartState();

  const renderSuccessItem = (
    key: number,
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
    title: string,
    description?: React.ReactNode,
  ): React.ReactNode => (
    <InformationStep key={key} icon={icon} loading={isLoading}>
      <StyledTitle component="p" variant="paragraph1" maxBreakpoint={Breakpoint.TABLET}>
        {title}
      </StyledTitle>
      {description && (
        <Typography variant="paragraph2" component="p" maxBreakpoint={Breakpoint.TABLET}>
          {description}
        </Typography>
      )}
    </InformationStep>
  );

  const handleButtonClick = () => navigationService.goToMineSider();

  const renderSuccessItems = (): React.ReactNode => {
    const items = [
      {
        icon: LocalShippingIcon,
        title: i18n.page.success.deliveryInformation[deliveryType].header,
        description: i18n.page.success.deliveryInformation[deliveryType].description,
      },
      {
        icon: ElectricalServicesIcon,
        title: i18n.page.success.equipment.header,
        description: I18nUtils.richMessage(i18n.page.success.equipment.description, {
          linkToGetStarted: (
            <Link variant="paragraph2" maxBreakpoint={Breakpoint.TABLET} href={config.GET_STARTED}>
              {i18n.page.success.equipment.linkToGetStarted}
            </Link>
          ),
        }),
      },
      {
        icon: FavoriteIcon,
        title: i18n.page.success.learnMore.header,
        description: I18nUtils.richMessage(i18n.page.success.learnMore.description, {
          linkToAltiboxPage: (
            <Link variant="paragraph2" maxBreakpoint={Breakpoint.TABLET} href={config.MAIN_PAGE}>
              {i18n.page.success.learnMore.linkToAltiboxPage}
            </Link>
          ),
        }),
      },
    ];

    return items.map((item, key) => renderSuccessItem(key, item.icon, item.title, item.description));
  };

  return (
    <StyledContainer>
      {isLoading ? (
        <StyledHeadingPlaceholder />
      ) : (
        <StyledHeading component="h1" variant="headline2">
          {i18n.page.success.header}
        </StyledHeading>
      )}
      <StyledSuccessItems>
        <ProductInformationStep icon={MailIcon} loading={isLoading}>
          <OrderDetails />
        </ProductInformationStep>
        {renderSuccessItems()}
      </StyledSuccessItems>
      {!isLoading && (
        <StyledButtonContainer>
          <Button onClick={handleButtonClick}>{i18n.page.success.cta}</Button>
        </StyledButtonContainer>
      )}
    </StyledContainer>
  );
};

export const SuccessPage = withOrderStep(OrderStep.SUCCESS)(SuccessPageComponent);
